// src/components/common/LoginModal.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaPhone, FaSpinner, FaCheck, FaArrowLeft } from 'react-icons/fa';
import { sendOTP, verifyOTP, setPhone, clearOTP, clearError, clearMessage, setShowLoginModal, getProfile } from '../../redux/slices/authSlice';
import { getColors } from '../../styles/colors';

const LoginModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  
  const { isLoading, error, otpSent, phone, isAuthenticated, message, isNewUser, profileCompleted } = useSelector((state) => state.auth);
  
  const [phoneInput, setPhoneInput] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [localError, setLocalError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);

  // ✅ Handle authentication success - redirect based on user type
  useEffect(() => {
    if (isAuthenticated && isOpen && !isRedirecting) {
      setIsRedirecting(true);
      setSuccessMessage('Login successful! Loading your profile...');
      
      const fetchAndRedirect = async () => {
        try {
          console.log('🔄 Fetching user profile after login...');
          
          // ✅ Fetch the latest profile from server
          const profileResult = await dispatch(getProfile()).unwrap();
          console.log('✅ Profile fetched:', profileResult);
          
          // ✅ Get user data from localStorage (updated by getProfile)
          const userData = JSON.parse(localStorage.getItem('user') || '{}');
          const isNew = localStorage.getItem('isNewUser') === 'true';
          const profileComplete = localStorage.getItem('profileCompleted') === 'true';
          
          // ✅ Check if user has a valid name
          const hasName = userData && userData.name && 
                         userData.name.trim() !== '' && 
                         userData.name !== 'User' && 
                         userData.name !== 'Account';
          
          console.log('🔍 Redirect check:', { 
            isNew, 
            profileComplete, 
            hasName, 
            userData,
            isAuthenticated: true 
          });
          
          // ✅ Clear modal states
          setShowOtpInput(false);
          setOtpInput('');
          setPhoneInput('');
          setTimer(30);
          setCanResend(false);
          
          setTimeout(() => {
            onClose();
            dispatch(setShowLoginModal(false));
            dispatch(clearOTP());
            dispatch(clearError());
            dispatch(clearMessage());
            
            // ✅ NEW USER FLOW: If isNewUser is true → go to profile form
            // ✅ EXISTING USER FLOW: If has profile → go to home
            if (isNew || (!profileComplete && !hasName)) {
              console.log('🆕 New user → Redirecting to profile form');
              navigate('/profile-form');
            } else {
              console.log('✅ Existing user with profile → Redirecting to home');
              navigate('/');
            }
            
            setIsRedirecting(false);
          }, 500);
          
        } catch (error) {
          console.error('❌ Failed to fetch profile:', error);
          
          // ✅ Even if profile fetch fails, try to redirect based on stored data
          const userData = JSON.parse(localStorage.getItem('user') || '{}');
          const isNew = localStorage.getItem('isNewUser') === 'true';
          const hasName = userData && userData.name && 
                         userData.name.trim() !== '' && 
                         userData.name !== 'User' && 
                         userData.name !== 'Account';
          
          setTimeout(() => {
            onClose();
            dispatch(setShowLoginModal(false));
            dispatch(clearOTP());
            dispatch(clearError());
            dispatch(clearMessage());
            
            // ✅ NEW USER: If isNew or no name → profile form
            if (isNew || !hasName) {
              navigate('/profile-form');
            } else {
              navigate('/');
            }
            setIsRedirecting(false);
          }, 500);
        }
      };
      
      fetchAndRedirect();
    }
  }, [isAuthenticated, isOpen, onClose, dispatch, navigate, isRedirecting]);

  // ✅ Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsRedirecting(false);
      setPhoneInput('');
      setOtpInput('');
      setShowOtpInput(false);
      setLocalError('');
      setSuccessMessage('');
      setTimer(30);
      setCanResend(false);
      dispatch(clearError());
      dispatch(clearMessage());
      dispatch(clearOTP());
    }
  }, [isOpen, dispatch]);

  // ✅ Timer for OTP resend
  useEffect(() => {
    let interval;
    if (otpSent && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [otpSent, timer]);

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    return cleaned.slice(0, 10);
  };

  const handleSendOTP = async () => {
    if (!phoneInput || phoneInput.length < 10) {
      setLocalError('Please enter a valid 10-digit phone number');
      return;
    }
    
    setLocalError('');
    setSuccessMessage('');
    dispatch(clearError());
    dispatch(clearMessage());
    dispatch(setPhone(phoneInput));
    
    try {
      const result = await dispatch(sendOTP(phoneInput)).unwrap();
      if (result) {
        setShowOtpInput(true);
        setTimer(30);
        setCanResend(false);
        setOtpInput('');
        setSuccessMessage('OTP sent successfully! Check your phone.');
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error('Send OTP error:', err);
      if (err && typeof err === 'string' && err.toLowerCase().includes('spam')) {
        setLocalError('Too many OTP attempts. Please wait 5 minutes before trying again.');
      } else if (err && typeof err === 'string') {
        setLocalError(err);
      } else if (err?.message) {
        setLocalError(err.message);
      } else {
        setLocalError('Failed to send OTP. Please try again.');
      }
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpInput || otpInput.length < 4) {
      setLocalError('Please enter the 6-digit OTP');
      return;
    }
    
    setLocalError('');
    setSuccessMessage('');
    dispatch(clearError());
    dispatch(clearMessage());
    
    try {
      const result = await dispatch(verifyOTP({ phone: phoneInput, otp: otpInput })).unwrap();
      console.log('✅ OTP verified successfully:', result);
      
      // ✅ The authentication will trigger the useEffect above
      // which will fetch the profile and redirect
      
    } catch (err) {
      console.error('Verify OTP error:', err);
      if (err && typeof err === 'string') {
        setLocalError(err);
      } else if (err?.message) {
        setLocalError(err.message);
      } else {
        setLocalError('Invalid OTP. Please try again.');
      }
    }
  };

  const handleResendOTP = () => {
    if (canResend) {
      dispatch(clearError());
      dispatch(clearMessage());
      dispatch(sendOTP(phoneInput));
      setTimer(30);
      setCanResend(false);
      setSuccessMessage('OTP resent successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const handleClose = () => {
    setIsRedirecting(false);
    setShowOtpInput(false);
    setOtpInput('');
    setPhoneInput('');
    setLocalError('');
    setSuccessMessage('');
    setTimer(30);
    setCanResend(false);
    
    onClose();
    dispatch(setShowLoginModal(false));
    dispatch(clearOTP());
    dispatch(clearError());
    dispatch(clearMessage());
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (!showOtpInput) {
        handleSendOTP();
      } else {
        handleVerifyOTP();
      }
    }
  };

  const handleBackToPhone = () => {
    setShowOtpInput(false);
    setOtpInput('');
    setLocalError('');
    setSuccessMessage('');
    dispatch(clearError());
    dispatch(clearMessage());
    dispatch(clearOTP());
  };

  // ✅ Clear input when clicking on it
  const handlePhoneFocus = () => {
    if (phoneInput) {
      setPhoneInput('');
    }
  };

  // ✅ If modal is not open, don't render anything
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center px-4"
        style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
        onClick={handleClose}
      >
        <motion.div
          className="rounded-2xl p-6 max-w-md w-full"
          initial={{ scale: 0.9, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.9, y: 20, opacity: 0 }}
          style={{ 
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {showOtpInput && (
                <button
                  onClick={handleBackToPhone}
                  className="p-2 rounded-full transition-all hover:scale-110"
                  style={{ backgroundColor: colors.inputBackground }}
                >
                  <FaArrowLeft className="text-sm" style={{ color: colors.text }} />
                </button>
              )}
              <h3 className="text-xl font-bold" style={{ color: colors.text }}>
                {showOtpInput ? 'Verify OTP' : 'Welcome to Foodies Tiffin!'}
              </h3>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-full transition-all hover:scale-110"
              style={{ backgroundColor: colors.inputBackground }}
            >
              <FaTimes style={{ color: colors.text }} />
            </button>
          </div>

          {/* Description */}
          <p className="text-sm mb-6" style={{ color: colors.textSecondary }}>
            {showOtpInput 
              ? `Enter the OTP sent to +91 ${phoneInput}`
              : 'Enter your phone number to continue'
            }
          </p>

          {/* Loading/Redirecting State */}
          {isRedirecting && (
            <div className="p-3 rounded-xl mb-4 text-sm flex items-center gap-2" style={{ 
              backgroundColor: '#EFF6FF',
              color: '#2563EB',
              border: '1px solid #BFDBFE'
            }}>
              <FaSpinner className="animate-spin" />
              {successMessage || 'Loading your profile...'}
            </div>
          )}

          {/* Success Message */}
          {successMessage && !isRedirecting && (
            <div className="p-3 rounded-xl mb-4 text-sm flex items-center gap-2" style={{ 
              backgroundColor: '#F0FDF4',
              color: '#16A34A',
              border: '1px solid #BBF7D0'
            }}>
              <FaCheck className="text-sm" />
              {successMessage}
            </div>
          )}

          {/* Error Message */}
          {(error || localError) && !isRedirecting && (
            <div className="p-3 rounded-xl mb-4 text-sm" style={{ 
              backgroundColor: '#FEF2F2',
              color: '#DC2626',
              border: '1px solid #FECACA'
            }}>
              {error || localError}
            </div>
          )}

          {/* Phone Input */}
          {!showOtpInput ? (
            <div className="mb-4">
              <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                Phone Number
              </label>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 px-3 py-2.5 rounded-xl" style={{ 
                  backgroundColor: colors.inputBackground,
                  border: `1px solid ${colors.border}`
                }}>
                  <span className="text-sm font-medium" style={{ color: colors.textSecondary }}>+91</span>
                </div>
                <input
                  type="tel"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(formatPhoneNumber(e.target.value))}
                  onFocus={handlePhoneFocus}
                  placeholder="98765 43210"
                  className="flex-1 p-3 rounded-xl text-sm transition-colors outline-none"
                  style={{
                    backgroundColor: colors.inputBackground,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                  }}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  maxLength={10}
                  disabled={isRedirecting}
                />
              </div>
              <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>
                We'll send you a 6-digit OTP for verification
              </p>
            </div>
          ) : (
            /* OTP Input */
            <div className="mb-4">
              <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                Enter OTP
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={otpInput}
                  onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit OTP"
                  className="flex-1 p-3 rounded-xl text-center text-lg font-bold tracking-widest transition-colors outline-none"
                  style={{
                    backgroundColor: colors.inputBackground,
                    color: colors.text,
                    border: `1px solid ${otpInput.length === 6 ? '#22C55E' : colors.border}`,
                  }}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  maxLength={6}
                  disabled={isRedirecting}
                />
                {otpInput.length === 6 && !isRedirecting && (
                  <FaCheck className="text-green-500 text-xl" />
                )}
              </div>
              
              <div className="flex items-center justify-between mt-2 text-sm">
                <span style={{ color: colors.textSecondary }}>
                  {canResend ? (
                    <button
                      onClick={handleResendOTP}
                      className="font-medium hover:underline transition-colors"
                      style={{ color: colors.primary }}
                      disabled={isRedirecting}
                    >
                      Resend OTP
                    </button>
                  ) : (
                    `Resend in ${timer}s`
                  )}
                </span>
                <button
                  onClick={handleBackToPhone}
                  className="text-xs hover:underline transition-colors"
                  style={{ color: colors.textSecondary }}
                  disabled={isRedirecting}
                >
                  Change number
                </button>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={showOtpInput ? handleVerifyOTP : handleSendOTP}
            disabled={isLoading || isRedirecting || (showOtpInput ? otpInput.length < 4 : phoneInput.length < 10)}
            className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ 
              backgroundColor: colors.primary,
              opacity: (showOtpInput ? otpInput.length >= 4 : phoneInput.length >= 10) && !isRedirecting ? 1 : 0.6
            }}
          >
            {isLoading || isRedirecting ? (
              <>
                <FaSpinner className="animate-spin" />
                {isRedirecting ? 'Loading...' : showOtpInput ? 'Verifying...' : 'Sending...'}
              </>
            ) : (
              <>
                {showOtpInput ? (
                  <>
                    <FaCheck /> Verify & Login
                  </>
                ) : (
                  <>
                    <FaPhone /> Send OTP
                  </>
                )}
              </>
            )}
          </button>

          <p className="text-xs text-center mt-4" style={{ color: colors.textSecondary + '80' }}>
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default LoginModal;